// Super Admin Dashboard - Global Management

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  MapPin, 
  Shield, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  Settings,
  BarChart3,
  UserCheck,
  Building,
  Crown,
  Search,
  Filter
} from 'lucide-react';
import Button from '../components/ui/Button';

interface Admin {
  id: number;
  name: string;
  email: string;
  role: 'chapter_admin' | 'moderator';
  chapter?: string;
  chapter_id?: number;
  status: 'active' | 'inactive' | 'pending';
  created_at: string;
  last_login?: string;
  permissions: string[];
}

interface Role {
  id: number;
  name: string;
  description: string;
  permissions: string[];
  created_at: string;
  assigned_count: number;
}

interface Chapter {
  id: number;
  name: string;
  city: string;
  state: string;
  admin_id?: number;
  admin_name?: string;
  status: 'active' | 'inactive';
  member_count: number;
}

const SuperAdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'admins' | 'chapters' | 'roles'>('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);

  // Mock data - replace with real API calls
  const [admins, setAdmins] = useState<Admin[]>([
    {
      id: 1,
      name: 'Mumbai Admin',
      email: 'mumbai@civicvoiceofindia.org',
      role: 'chapter_admin',
      chapter: 'Mumbai Central Chapter',
      chapter_id: 1,
      status: 'active',
      created_at: '2024-01-15',
      last_login: '2025-09-18',
      permissions: ['manage_users', 'manage_events', 'view_reports']
    },
    {
      id: 2,
      name: 'Delhi Admin',
      email: 'delhi@civicvoiceofindia.org',
      role: 'chapter_admin',
      chapter: 'Delhi NCR Chapter',
      chapter_id: 2,
      status: 'active',
      created_at: '2024-01-20',
      last_login: '2025-09-17',
      permissions: ['manage_users', 'manage_events', 'view_reports']
    },
    {
      id: 3,
      name: 'Bengaluru Admin',
      email: 'bengaluru@civicvoiceofindia.org',
      role: 'chapter_admin',
      chapter: 'Bengaluru Tech Chapter',
      chapter_id: 3,
      status: 'pending',
      created_at: '2024-09-15',
      permissions: ['manage_users', 'manage_events', 'view_reports']
    }
  ]);

  const [roles, setRoles] = useState<Role[]>([
    {
      id: 1,
      name: 'Chapter Administrator',
      description: 'Full administrative access to chapter operations',
      permissions: ['manage_users', 'manage_events', 'view_reports', 'manage_content'],
      created_at: '2024-01-01',
      assigned_count: 20
    },
    {
      id: 2,
      name: 'Content Moderator',
      description: 'Moderate content and manage community interactions',
      permissions: ['moderate_content', 'manage_comments', 'view_reports'],
      created_at: '2024-01-01',
      assigned_count: 15
    },
    {
      id: 3,
      name: 'Event Coordinator',
      description: 'Organize and manage chapter events',
      permissions: ['manage_events', 'view_attendees', 'send_notifications'],
      created_at: '2024-01-01',
      assigned_count: 8
    }
  ]);

  const [chapters, setChapters] = useState<Chapter[]>([
    { id: 1, name: 'Mumbai Central Chapter', city: 'Mumbai', state: 'Maharashtra', admin_id: 1, admin_name: 'Mumbai Admin', status: 'active', member_count: 0 },
    { id: 2, name: 'Delhi NCR Chapter', city: 'New Delhi', state: 'Delhi', admin_id: 2, admin_name: 'Delhi Admin', status: 'active', member_count: 0 },
    { id: 3, name: 'Bengaluru Tech Chapter', city: 'Bengaluru', state: 'Karnataka', admin_id: 3, admin_name: 'Bengaluru Admin', status: 'inactive', member_count: 0 },
    { id: 4, name: 'Chennai Chapter', city: 'Chennai', state: 'Tamil Nadu', status: 'inactive', member_count: 0 },
    { id: 5, name: 'Kolkata Chapter', city: 'Kolkata', state: 'West Bengal', status: 'inactive', member_count: 0 }
  ]);

  const stats = {
    totalAdmins: admins.length,
    activeChapters: chapters.filter(c => c.status === 'active').length,
    totalRoles: roles.length,
    pendingApprovals: admins.filter(a => a.status === 'pending').length
  };

  const handleCreateAdmin = () => {
    setShowCreateModal(true);
  };

  const handleEditAdmin = (admin: Admin) => {
    setSelectedAdmin(admin);
    setShowCreateModal(true);
  };

  const handleDeleteAdmin = (adminId: number) => {
    if (confirm('Are you sure you want to delete this admin?')) {
      setAdmins(admins.filter(a => a.id !== adminId));
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      active: 'bg-green-100 text-green-800 border-green-200',
      inactive: 'bg-gray-100 text-gray-800 border-gray-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return badges[status as keyof typeof badges] || badges.inactive;
  };

  const filteredAdmins = admins.filter(admin => 
    admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.chapter?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container-width section-padding">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Crown className="w-8 h-8 text-yellow-500 mr-3" />
                Super Admin Dashboard
              </h1>
              <p className="text-gray-600 mt-1">Global platform management and administration</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={handleCreateAdmin}
                className="flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Create Admin</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Admins</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalAdmins}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Chapters</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeChapters}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <MapPin className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Roles</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalRoles}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Approvals</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pendingApprovals}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', label: 'Overview', icon: BarChart3 },
                { id: 'admins', label: 'Admin Management', icon: Users },
                { id: 'chapters', label: 'Chapter Management', icon: Building },
                { id: 'roles', label: 'Role Management', icon: Shield }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'admins' && (
              <div>
                {/* Search and Filter */}
                <div className="flex items-center justify-between mb-6">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search admins..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <Button variant="outline" className="ml-4">
                    <Filter className="w-4 h-4 mr-2" />
                    Filter
                  </Button>
                </div>

                {/* Admins Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admin</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chapter</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredAdmins.map((admin) => (
                        <tr key={admin.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <img
                                className="h-10 w-10 rounded-full"
                                src={`https://ui-avatars.com/api/?name=${admin.name.replace(' ', '+')}&background=3b82f6&color=fff`}
                                alt={admin.name}
                              />
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{admin.name}</div>
                                <div className="text-sm text-gray-500">{admin.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{admin.chapter || 'Not assigned'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900 capitalize">{admin.role.replace('_', ' ')}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusBadge(admin.status)}`}>
                              {admin.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {admin.last_login ? new Date(admin.last_login).toLocaleDateString() : 'Never'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleEditAdmin(admin)}
                                className="text-primary-600 hover:text-primary-900"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteAdmin(admin.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'chapters' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Chapter Management</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {chapters.map((chapter) => (
                    <div key={chapter.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{chapter.name}</h4>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(chapter.status)}`}>
                          {chapter.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{chapter.city}, {chapter.state}</p>
                      <p className="text-sm text-gray-500">
                        Admin: {chapter.admin_name || 'Not assigned'}
                      </p>
                      <p className="text-sm text-gray-500">
                        Members: {chapter.member_count}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'roles' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-medium text-gray-900">Role Management</h3>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Role
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {roles.map((role) => (
                    <div key={role.id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-gray-900">{role.name}</h4>
                        <span className="text-sm text-gray-500">{role.assigned_count} assigned</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">{role.description}</p>
                      <div className="mb-4">
                        <p className="text-xs font-medium text-gray-700 mb-2">Permissions:</p>
                        <div className="flex flex-wrap gap-1">
                          {role.permissions.map((permission) => (
                            <span
                              key={permission}
                              className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded"
                            >
                              {permission.replace('_', ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm">
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50">
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'overview' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6">Platform Overview</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Recent Activity */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h4 className="font-medium text-gray-900 mb-4">Recent Activity</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <UserCheck className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-900">New admin approved</p>
                          <p className="text-xs text-gray-500">Bengaluru Admin - 2 hours ago</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <Building className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-900">Chapter activated</p>
                          <p className="text-xs text-gray-500">Chennai Chapter - 1 day ago</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h4 className="font-medium text-gray-900 mb-4">Quick Actions</h4>
                    <div className="space-y-3">
                      <Button variant="outline" className="w-full justify-start">
                        <Plus className="w-4 h-4 mr-2" />
                        Create New Admin
                      </Button>
                      <Button variant="outline" className="w-full justify-start">
                        <Shield className="w-4 h-4 mr-2" />
                        Manage Roles
                      </Button>
                      <Button variant="outline" className="w-full justify-start">
                        <Settings className="w-4 h-4 mr-2" />
                        Platform Settings
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
